import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import i18next from 'i18next';
import { api, ApiError, clearLegacyAuthToken, ensureCsrf, UNAUTHENTICATED_EVENT } from '@/lib/api';
import { apiUrl, marketingUrl } from '@/lib/hosts';
import { clearAuthRedirectHistory } from '@/lib/authRedirectGuard';

const SHOP_KEY = 'current_shop_id';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  locale: string;
  is_active: boolean;
  is_admin: boolean;
  needs_onboarding: boolean;
  two_factor_enabled: boolean;
  login_notification_enabled: boolean;
  email_verified_at: string | null;
  has_password?: boolean;
  oauth_provider?: string | null;
  avatar_url?: string | null;
  shops: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, extra?: Record<string, string>) => Promise<User | null>;
  logout: () => Promise<void>;
  refetchUser: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function syncCurrentShopId(user: User | null): void {
  if (!user || !user.shops || user.shops.length === 0) {
    localStorage.removeItem(SHOP_KEY);
    return;
  }

  const savedId = localStorage.getItem(SHOP_KEY);
  const hasValidSavedShop = savedId !== null && user.shops.some((shop) => shop.id === savedId);
  if (hasValidSavedShop) return;

  const firstShop = user.shops[0];
  if (firstShop) {
    localStorage.setItem(SHOP_KEY, firstShop.id);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  useEffect(() => {
    clearLegacyAuthToken();
    const handleUnauthenticated = () => {
      queryClient.setQueryData(['auth', 'user'], null);
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, handleUnauthenticated);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, handleUnauthenticated);
  }, [queryClient]);

  const {
    data: user,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: async () => {
      try {
        const res = await fetch(apiUrl('/api/v1/auth/check'), {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const json = await res.json();
        if (!json.authenticated) {
          localStorage.removeItem(SHOP_KEY);
          return null;
        }
        const authenticatedUser = json.user as User;
        syncCurrentShopId(authenticatedUser);
        return authenticatedUser;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    if (
      user?.locale &&
      (user.locale === 'fr' || user.locale === 'en') &&
      i18next.isInitialized &&
      i18next.language !== user.locale
    ) {
      void i18next.changeLanguage(user.locale);
    }
  }, [user?.locale]);

  const loginMutation = useMutation({
    mutationFn: async ({
      email,
      password,
      extra,
    }: {
      email: string;
      password: string;
      extra?: Record<string, string>;
    }) => {
      await ensureCsrf();
      const response = await api.post<
        | { user: User; auth_mode: 'session' }
        | { user: User; auth_mode: 'token'; token: string }
        | { two_factor_required: true; user_id: string }
      >('/auth/login', {
        email,
        password,
        ...extra,
      });
      return response.data;
    },
    onSuccess: (data) => {
      if ('two_factor_required' in data) return;
      clearAuthRedirectHistory();
      syncCurrentShopId(data.user);
      Sentry.setUser({ id: data.user.id, email: data.user.email });
    },
  });

  const login = useCallback(
    async (email: string, password: string, extra?: Record<string, string>) => {
      const result = await loginMutation.mutateAsync({ email, password, extra });
      if ('two_factor_required' in result) return null;

      // The login response intentionally stays compact and does not contain
      // relations such as `shops`. Confirm the new Sanctum session and hydrate
      // the complete User before guards/layouts can render or navigate.
      const confirmation = await refetch();
      if (!confirmation.data) {
        throw new Error("La session n'a pas pu être confirmée. Réessaie la connexion.");
      }
      return confirmation.data;
    },
    [loginMutation, refetch],
  );

  const logout = useCallback(async () => {
    const attempt = async (): Promise<boolean> => {
      try {
        await api.post('/auth/logout');
      } catch (error) {
        // A 401 means the server session is already gone: local cleanup and the
        // login redirect are still the correct outcome.
        if (!(error instanceof ApiError && error.status === 401)) return false;
      }

      clearLegacyAuthToken();
      clearAuthRedirectHistory();
      localStorage.removeItem(SHOP_KEY);
      Sentry.setUser(null);
      queryClient.setQueryData(['auth', 'user'], null);
      queryClient.clear();

      if (typeof window !== 'undefined') {
        window.location.replace(marketingUrl('/login'));
      }
      return true;
    };

    if (await attempt()) return;

    toast.error('Déconnexion impossible', {
      description: 'La session est restée ouverte. Vérifie ta connexion puis réessaie.',
      duration: 10_000,
      action: {
        label: 'Réessayer',
        onClick: () => {
          void attempt().then((success) => {
            if (!success) toast.error('Le serveur reste injoignable. Réessaie dans un instant.');
          });
        },
      },
    });
  }, [queryClient]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isAdmin: user?.is_admin ?? false,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refetchUser: refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
